const strategicCompass = {
  back: '← Back to team space',

  hero: {
    eyebrow: 'TOF Persona · The Strategic Compass',
    titleLead: 'What the world asks of you, translated into ',
    titleAccent: 'who your team is',
    titleTail: '.',
    sub: 'For management teams and boards. External trends connected to your team personas — a compass for the next three to five years.',
    cta: 'Explore the approach',
    meta: '3–5 YEARS · FOUR-MONTH PROGRAMME',
  },

  why: {
    eyebrow: 'Why the Compass',
    titleLead: 'Strategy without a view of people is a ',
    titleAccent: 'compass without north',
    titleTail: '.',
    lead: 'Many strategy programmes look outward — trends, market, technology. Others look inward — culture, leadership, teams. The Strategic Compass does both. Trends always land differently depending on who actually works there.',
    // The paid modules side by side. Module 1 (the free app) is deliberately
    // absent: it is the way in, not an alternative to this programme.
    compareHeads: ['TOF Persona Team', 'The Organisation Landscape', 'The Strategic Compass'],
    compareRows: [
      {
        label: 'Perspective',
        cells: ['Inward — the team', 'Inward — the organisation', 'Outside in'],
      },
      {
        label: 'Time horizon',
        cells: ['The coming quarters', 'This year', '3–5 years'],
      },
      {
        label: 'Output',
        cells: ['Team dashboard', 'Organisation landscape', 'Strategic direction document'],
      },
      {
        label: 'Audience',
        cells: ['Team leads, HR', 'Leadership, HR', 'Management team and board'],
      },
    ],
  },

  trendsSection: {
    eyebrow: 'The 8 trends we follow',
    titleLead: 'The world ',
    titleAccent: 'your team',
    titleTail: ' will be working in.',
    lead: 'Curated from Colliers Insights, the WorkWire Trend Report 2025–2026, Social Based Working and the AI Index Report 2026. Reviewed every year, so the compass keeps moving with you.',
  },

  trends: [
    {
      num: '01',
      title: 'Experience based working',
      body: 'From "what you do" to "how it feels". The workplace is judged on atmosphere, experience and the quality of the day — not on a chair and a desk.',
    },
    {
      num: '02',
      title: 'From attendance to value',
      body: 'No longer "two days in the office", but: why do you come together, what does it deliver, and what does the environment need to do to support that?',
    },
    {
      num: '03',
      title: 'Peak management — taming the camel week',
      body: '75% want to be in the office on a Tuesday or Thursday. Steering the moments people gather becomes crucial — not the number of days, but the quality of those days.',
    },
    {
      num: '04',
      title: 'DEIB as design, not as policy',
      body: 'Inclusion shifts from ambition to design question: accessibility, low-stimulus zones, psychological safety — visible in choices, not in policy documents.',
    },
    {
      num: '05',
      title: 'Social based working',
      body: 'The workplace as social fabric. Connection does not appear simply because more people are present — it grows from meaningful moments and social architecture.',
    },
    {
      num: '06',
      title: 'The service revolution',
      body: 'The office as a hospitality environment. Services and experience — not square metres — decide whether the place is attractive to people.',
    },
    {
      num: '07',
      title: 'From mega campus to tailored spaces',
      body: 'Smaller, purposeful locations closer to where people live. The central head office gives way to a network of more personal places.',
    },
    {
      num: '08',
      title: 'AI in the workplace',
      body: 'AI as a steering instrument: continuously measuring experience, friction and effectiveness. Not to monitor people, but to make their environment better.',
    },
  ],

  methodSection: {
    eyebrow: 'The approach',
    titleLead: 'Five steps to ',
    titleAccent: 'your compass',
    titleTail: '.',
    lead: 'The Strategic Compass is built over four months, in close collaboration with your management team or board. Not a report that gathers dust — a living document embedded in your rhythm.',
  },

  steps: [
    {
      num: '01',
      name: 'Intake',
      meta: '60 min · management team / board',
      body: 'What is your organisational ambition for the next 3–5 years? What movement do you want to make? What is already in place, and what is missing?',
    },
    {
      num: '02',
      name: 'Trend reflection',
      meta: 'Preparation · 2–3 weeks',
      body: 'Which of the 8 trends hit your organisation hardest? Which reinforce your ambition, and which cut across it? This is where the outside world comes sharply into focus.',
    },
    {
      num: '03',
      name: 'Persona overlay',
      meta: 'From the team pictures and the Organisation Landscape',
      body: 'How do your personas relate to those trends? A Maker-heavy team in a DEIB-driven organisation needs something very different from a Connector-heavy team.',
    },
    {
      num: '04',
      name: 'Design conversation',
      meta: 'Half a day · management team / board',
      body: 'Trend radar and persona overlay come together on the table. We make choices about leadership, work environment, culture and technology — grounded and easy to explain.',
    },
    {
      num: '05',
      name: 'The Compass',
      meta: 'Living document · updated yearly',
      body: 'A premium PDF of around 20 pages plus a one-page overview for management meetings. Not a plan in a drawer — a compass that moves with you each year.',
    },
  ],

  deliverableSection: {
    eyebrow: 'What you get in hand',
    titleLead: 'A document that ',
    titleAccent: 'opens',
    titleTail: ' management meetings, rather than closing them.',
  },

  chapters: [
    'Context',
    'World view — trend radar',
    'Team view — persona mix',
    'Intersection — where trends and team meet',
    'Choices — 5–7 strategic directions',
    'Compass — one-pager for the management team',
    'Yearly rhythm — when do we look back?',
  ],

  deliverables: [
    { strong: 'Trend radar', text: '8 trends, weighted by relevance to your organisation' },
    { strong: 'Persona overlay', text: 'how your team mix relates to those trends' },
    { strong: 'Strategic choices', text: 'for leadership, work environment, culture and technology' },
    { strong: 'Compass one-pager', text: 'a printable overview, ready for the management meeting' },
    { strong: 'Yearly rhythm', text: 'fixed moments to recalibrate the compass' },
  ],

  stackSection: {
    eyebrow: 'Where the Compass fits',
    titleLead: 'Four modules, one ',
    titleAccent: 'story',
    titleTail: '.',
    lead: 'The Compass stands on the shoulders of what comes before it. Only once you know who works here, how teams work together and what the organisation looks like can you look meaningfully at where you want to go.',
  },

  stack: [
    {
      num: '01 · The app',
      title: 'Who are you?',
      body: 'The app on your phone. Your own profile, your workplace needs, your conversation prep.',
    },
    {
      num: '02 · TOF Persona Team',
      title: 'How do you work together?',
      body: 'Team dashboard. Persona mix, tensions, workplace needs and quick wins.',
    },
    {
      num: '03 · The Organisation Landscape',
      title: 'What do you see across teams?',
      body: 'All teams side by side, with interpretation for leadership and HR.',
    },
    {
      num: '04 · The Strategic Compass',
      title: 'Where are we heading?',
      body: 'Outside in — trends and personas translated into strategy.',
    },
  ],

  cta: {
    titleLead: 'Ready for a ',
    titleAccent: 'sharper compass',
    titleTail: '?',
    sub: 'Book a no-obligation 30-minute conversation. Together we will see whether this fits where your organisation stands today.',
    primary: 'Book a conversation →',
    secondary: 'Back to modules',
  },
};

export default strategicCompass;
